import { useEffect, useId, useRef, useState } from 'react'
import { fetchCameras } from '../api'
import { NO_FILTER, allowsCamera, allowsSite, isNarrowed, toggleCamera, toggleSite, type SiteFilter } from '../siteFilter'
import type { Site } from '../types'

interface Props {
  sites: Site[]
  filter: SiteFilter
  onChange: (next: SiteFilter) => void
}

interface ButtonProps extends Props {
  /** In the top bar: its words drop below the small breakpoint, so a phone's bar fits. */
  compact?: boolean
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

// FilterButton is the Filter button, on the wall or in the top bar, and the
// panel it opens: the Sites sidebar's checkboxes (#281). A tick applies at
// once, as the sidebar's did, so Done only closes.
export function FilterButton({ sites, filter, onChange, compact = false }: ButtonProps) {
  const [open, setOpen] = useState(false)
  const button = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)

  const close = () => {
    setOpen(false)
    button.current?.focus()
  }

  useEffect(() => {
    if (!open) return
    const outside = (e: MouseEvent) => {
      if (!panel.current?.contains(e.target as Node) && !button.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', outside)
    return () => document.removeEventListener('mousedown', outside)
  }, [open])

  const shownSites = sites.filter((s) => allowsSite(filter, s.id)).length
  const label = !isNarrowed(filter)
    ? 'Filter'
    : shownSites < sites.length
      ? `Filter · ${shownSites} of ${sites.length} sites`
      : 'Filter · some cameras'

  return (
    <div className="relative">
      <button
        ref={button}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
        className={`h-9 px-3.5 flex items-center gap-2 rounded-md border text-sm font-semibold transition-colors ${
          isNarrowed(filter)
            ? 'border-orange-400 bg-orange-500/10 text-orange-300 hover:bg-orange-500/15'
            : 'border-zinc-800 bg-zinc-900 text-zinc-100 hover:bg-zinc-800'
        }`}
      >
        <svg aria-hidden="true" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" d="M4 6h16M7 12h10M10 18h4" />
        </svg>
        <span className={compact ? 'hidden sm:inline' : undefined}>{label}</span>
      </button>
      {open && (
        <div ref={panel} onKeyDown={(e) => e.key === 'Escape' && close()}>
          <FilterPanel sites={sites} filter={filter} onChange={onChange} onClose={close} />
        </div>
      )}
    </div>
  )
}

function FilterPanel({ sites, filter, onChange, onClose }: Props & { onClose: () => void }) {
  const [camerasBySite, setCamerasBySite] = useState<Record<string, string[]>>({})
  const [expanded, setExpanded] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const title = useId()
  const search = useRef<HTMLInputElement>(null)

  // Fetched when the panel opens, as mobile's filter does: the sidebar held
  // every site's list the whole time to draw its tree.
  useEffect(() => {
    let cancelled = false
    for (const s of sites) {
      fetchCameras(s.id)
        .then((cams) => { if (!cancelled) setCamerasBySite((prev) => ({ ...prev, [s.id]: cams ?? [] })) })
        .catch(() => { if (!cancelled) setCamerasBySite((prev) => ({ ...prev, [s.id]: prev[s.id] ?? [] })) })
    }
    search.current?.focus()
    return () => { cancelled = true }
  }, [sites])

  const allSiteIds = sites.map((s) => s.id)
  const camerasOf = (siteId: string) => camerasBySite[siteId] ?? []
  const shownOf = (siteId: string) => camerasOf(siteId).filter((c) => allowsCamera(filter, siteId, c))

  const selectAll = (siteId: string) => {
    const cams = camerasOf(siteId)
    let next = allowsSite(filter, siteId) ? filter : toggleSite(filter, siteId, allSiteIds)
    for (const c of cams) if (!allowsCamera(next, siteId, c)) next = toggleCamera(next, siteId, c, cams)
    onChange(next)
  }
  const selectNone = (siteId: string) => {
    const cams = camerasOf(siteId)
    let next = filter
    for (const c of cams) if (allowsCamera(next, siteId, c)) next = toggleCamera(next, siteId, c, cams)
    onChange(next)
  }

  const needle = query.trim().toLowerCase()
  // A site whose name matches brings all its cameras, as the box promises.
  const siteMatches = (siteId: string) => (sites.find((s) => s.id === siteId)?.name || siteId).toLowerCase().includes(needle)
  const matchesOf = (siteId: string) => camerasOf(siteId).filter((c) => siteMatches(siteId) || c.toLowerCase().includes(needle))
  const matchCount = needle ? sites.reduce((n, s) => n + matchesOf(s.id).length, 0) : 0
  const totalCameras = sites.reduce((n, s) => n + camerasOf(s.id).length, 0)
  const shownCameras = sites.reduce((n, s) => n + shownOf(s.id).length, 0)
  const shownSites = sites.filter((s) => allowsSite(filter, s.id)).length

  const cameraRow = (siteId: string, camera: string) => (
    <li key={camera}>
      <label className="flex items-center gap-2.5 min-h-9 pl-9 pr-2 rounded-md text-sm text-zinc-200 hover:bg-zinc-800/60 cursor-pointer">
        <input
          type="checkbox"
          checked={allowsCamera(filter, siteId, camera)}
          onChange={() => onChange(toggleCamera(filter, siteId, camera, camerasOf(siteId)))}
          className="w-4 h-4 shrink-0 accent-orange-400"
        />
        <span className="truncate">{camera}</span>
      </label>
    </li>
  )

  return (
    <div
      role="dialog"
      aria-labelledby={title}
      className="absolute right-0 mt-2 z-50 w-[24.5rem] max-w-[calc(100vw-2rem)] max-sm:fixed max-sm:inset-x-4 max-sm:top-16 max-sm:mt-0 max-sm:w-auto max-sm:max-w-none flex flex-col rounded-lg border border-zinc-700 bg-zinc-900 shadow-2xl origin-top-right transition-[opacity,scale] duration-150 ease-out starting:opacity-0 starting:scale-97 motion-reduce:transition-none"
    >
      <div className="flex items-center gap-2 px-3.5 pt-3.5 pb-2.5">
        <h2 id={title} className="text-base font-semibold text-zinc-100">Filter</h2>
        <div className="flex-1" />
        {isNarrowed(filter) && (
          <button type="button" onClick={() => onChange(NO_FILTER)} className="h-8 max-sm:h-11 px-2.5 rounded-md text-sm font-semibold text-orange-300 hover:bg-zinc-800">
            Clear
          </button>
        )}
        <button type="button" aria-label="Close filter" onClick={onClose} className="w-8 h-8 max-sm:w-11 max-sm:h-11 flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800">
          <svg aria-hidden="true" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="px-3.5 pb-2.5 relative">
        <svg aria-hidden="true" className="absolute left-6 top-3 w-4 h-4 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <circle cx="11" cy="11" r="7" />
          <path strokeLinecap="round" d="m20 20-3.5-3.5" />
        </svg>
        <input
          ref={search}
          type="search"
          aria-label="Search sites and cameras"
          placeholder="Search sites and cameras…"
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full h-10 pl-9 pr-3 rounded-md border border-zinc-500 bg-zinc-950 text-sm max-sm:text-base text-zinc-100 placeholder:text-zinc-500 focus:border-orange-400"
        />
      </div>
      {needle && (
        <p aria-live="polite" className="px-4 pb-2 text-[13px] text-zinc-400">
          {matchCount === 0 ? `No cameras match “${query.trim()}”` : `${plural(matchCount, 'camera matches', 'cameras match')} “${query.trim()}”`}
        </p>
      )}
      <ul className="max-h-[28rem] relative overflow-y-auto overscroll-contain scrollbar-thin px-2 pb-2 border-t border-zinc-800 pt-1.5 flex flex-col gap-0.5">
        {sites.map((site) => {
          const name = site.name || site.id
          const cams = camerasOf(site.id)
          const matches = needle ? matchesOf(site.id) : null
          if (matches && matches.length === 0) return null
          const shown = shownOf(site.id).length
          const on = allowsSite(filter, site.id)
          const partly = on && cams.length > 0 && shown > 0 && shown < cams.length
          const isOpen = !matches && expanded === site.id
          return (
            <li key={site.id}>
              <div className="flex items-center gap-1 min-h-10 pl-2 pr-1 rounded-md hover:bg-zinc-800/60">
                <label className="flex-1 min-w-0 flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={on && (cams.length === 0 || shown > 0) && !partly}
                    ref={(el) => { if (el) el.indeterminate = partly }}
                    onChange={() => onChange(toggleSite(filter, site.id, allSiteIds))}
                    className="w-4 h-4 shrink-0 accent-orange-400"
                  />
                  <span className="truncate text-sm font-semibold text-zinc-100">{name}</span>
                </label>
                {cams.length > 0 && <span className="shrink-0 text-xs font-mono text-zinc-400 tabular-nums">{`${shown} of ${cams.length}`}</span>}
                {!matches && cams.length > 0 && (
                  <button
                    type="button"
                    aria-label={`${isOpen ? 'Hide' : 'Show'} ${name} cameras`}
                    aria-expanded={isOpen}
                    onClick={() => setExpanded(isOpen ? null : site.id)}
                    className="w-8 h-8 max-sm:w-11 max-sm:h-11 shrink-0 flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
                  >
                    <svg aria-hidden="true" className={`w-3.5 h-3.5 transition-transform duration-150 motion-reduce:transition-none ${isOpen ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="m9 5 7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>
              {isOpen && (
                <div className="flex gap-1.5 pl-9 pb-1 pt-0.5">
                  <button type="button" aria-label={`Select all of ${name}`} onClick={() => selectAll(site.id)} className="h-8 max-sm:h-11 px-2.5 rounded-md border border-zinc-800 text-[13px] text-zinc-200 hover:bg-zinc-800">
                    Select all
                  </button>
                  <button type="button" aria-label={`Select none of ${name}`} onClick={() => selectNone(site.id)} className="h-8 max-sm:h-11 px-2.5 rounded-md border border-zinc-800 text-[13px] text-zinc-200 hover:bg-zinc-800">
                    Select none
                  </button>
                </div>
              )}
              {(matches ?? (isOpen ? cams : [])).length > 0 && (
                <ul className="flex flex-col gap-0.5">{(matches ?? cams).map((c) => cameraRow(site.id, c))}</ul>
              )}
            </li>
          )
        })}
      </ul>
      <div className="flex items-center gap-3 px-3.5 py-3 border-t border-zinc-800">
        <span className="flex-1 text-[13px] font-mono text-zinc-400 tabular-nums">
          {`Showing ${shownSites} of ${plural(sites.length, 'site', 'sites')}`}
          {totalCameras > 0 && ` · ${shownCameras} of ${plural(totalCameras, 'camera', 'cameras')}`}
        </span>
        <button type="button" onClick={onClose} className="h-9 px-4 rounded-md bg-orange-400 text-zinc-950 text-sm font-bold hover:bg-orange-300">
          Done
        </button>
      </div>
    </div>
  )
}
